import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { ListVacantPositionsDocument, GetProjectByLfdNrDocument } from '~/gql/ucpw/graphql';
import { withRenderers } from '~/utils';
import { tables, controlling } from '~/pages/master-data/meta/data/controlling.schema';
import { useDataLoader } from '~/hooks/useDataLoader';
import { useFetchDataWithFilter } from '~/hooks/useFetchDataWithFilter';
import { ControllingStates } from '~/pages/master-data/types';
import { client } from '~/apollo';
import log from '~/log';

export const useControlling = ({
    branchId,
    employeeId,
    only28DaysAndOlder,
}: ControllingStates = {}) => {
    log.debug('useControlling', { branchId, employeeId, only28DaysAndOlder });
    const navigate = useNavigate();
    const context = { clientName: 'ucpw' };

    // ===============================================
    // LAZY LISTING
    // ===============================================

    const [load, { data, loading: vacantPositionslazyLoading }] = useLazyQuery(
        ListVacantPositionsDocument,
        { context }
    );
    const vacantPositions: any = data?.app?.item?.items || [];
    const totalSalesVolume = data?.app?.item.gesamtUmsatz || 0;
    const { pageCount = 0, totalCount: searchCount = 0 } = data?.app?.item?.pageInfo || {};

    const { fetchData } = useDataLoader(load);
    const fetchDataWithFilters = useFetchDataWithFilter(fetchData, {
        niederlassungId: branchId,
        mitarbeiterId: employeeId,
        only28DaysAndOlder,
    });

    // ===============================================
    // Redirect to project
    // ===============================================

    const columns = React.useMemo(() => withRenderers(tables.controlling.columns), []);
    const pageSize = controlling?.tableProps?.pageSize;
    const loading = vacantPositionslazyLoading;

    const onClick = async ({ original }: any) => {
        const parts = original?.subprojektnr?.split('-');
        // For pattern like "23-02-157142-1", we want the third part (index 2)
        const lfdNr = parts?.[2];
        const subprojektLfdNr = parts?.[3];
        if (lfdNr) {
            const { data } = await client.query({
                query: GetProjectByLfdNrDocument,
                variables: { lfdNr: parseInt(lfdNr, 10) },
                fetchPolicy: 'network-only',
                context,
            });
            const project = data?.app?.item?.item;
            const subproject = project?.subprojekt?.find(
                (subproject: any) => subproject?.lfdNr === parseInt(subprojektLfdNr, 10)
            );
            if (project?.id && subproject?.id) {
                navigate(`/projekte/${project.id}/${subproject.id}/details`);
            }
        }
    };

    return {
        totalSalesVolume,
        sortBy: [{ id: 'letzterTermin', desc: true }],
        searchCount,
        columns,
        data: vacantPositions,
        loading,
        fetchData: fetchDataWithFilters,
        pageCount,
        pageSize,
        onClick,
    };
};
